import { arrayOf, bool, func, number } from 'prop-types';
import React from 'react';
import { GoDash, GoPlus, GoTrashcan } from 'react-icons/go';
import Moment from 'react-moment';
import { Button, ButtonGroup, Col, Row } from 'reactstrap';

import EntityLabel from '../../../generic/EntityLabel';
import SignedCurrencyFormat from '../../../generic/SignedCurrencyFormat';
import withConfirm from '../../../generic/with-confirm';
import { bookingEntryShape, productShape } from '../../../prop-types';
import { UI_SHORT_DATE_FORMAT } from '../../../utils/constants';
import UpdateBookingEntryModal from '../UpdateBookingEntry';

const DeleteWithConfirmation = withConfirm(
  'Do you really want to delete this booking entry?',
)(Button);

function BookingEntryLine({
  entry,
  products,
  showUpdateButton,
  onAdd,
  onRemove,
  onDelete,
  invoiceId,
  onIsSelected,
  showSelectBox,
  onToggleSelection,
  isEditing,
}) {
  return (
    <div
      id={`booking-entry-line-${entry.id}`}
      className="c-balance-line c-invoice-line c-invoice-line__booking-entry-line"
    >
      <Row className="align-items-center">
        <Col
          sm={2}
          data-qa-field="inv-id"
          className="d-flex justify-content-between align-items-center"
        >
          <div className="d-flex align-items-center">
            {showSelectBox && isEditing && (
              <input
                type="checkbox"
                checked={onIsSelected(entry.id)}
                className="mr-2"
                onChange={() => onToggleSelection(`select-${entry.id}`)}
              />
            )}
            {showUpdateButton && isEditing ? (
              <UpdateBookingEntryModal
                id={entry.id}
                invoiceId={invoiceId}
                className="p-0 pl-1"
                products={products}
              />
            ) : (
              <EntityLabel id={entry.id} resourceType="bookingEntry" />
            )}
          </div>
          <div className="small text-muted">{entry.entryType}</div>
        </Col>
        <Col sm={2} data-qa-field="inv-createdBy">
          <div>{entry.createdBy}</div>
          <div className="small text-muted">Created by</div>
        </Col>
        <Col sm={1} data-qa-field="inv-serviceStartDate">
          {entry.serviceStartDate && (
            <>
              <Moment format={UI_SHORT_DATE_FORMAT}>
                {entry.serviceStartDate}
              </Moment>
              <div className="small text-muted text-nowrap">Service start</div>
            </>
          )}
        </Col>
        <Col sm={1} data-qa-field="inv-serviceEndDate">
          {entry.serviceEndDate && (
            <>
              <Moment format={UI_SHORT_DATE_FORMAT}>
                {entry.serviceEndDate}
              </Moment>
              <div className="small text-muted">Service end</div>
            </>
          )}
        </Col>
        <Col sm={2} data-qa-field="inv-created">
          {entry.created && (
            <>
              <Moment format={UI_SHORT_DATE_FORMAT}>{entry.created}</Moment>
              <div className="small text-muted">Booking date</div>
            </>
          )}
        </Col>
        <Col sm={1} data-qa-field="inv-debitAmount" className="pr-0">
          <SignedCurrencyFormat value={entry.debitAmount - entry.discount} />
          <div className="small text-muted">Debit Amount</div>
        </Col>
        <Col sm={2} data-qa-field="inv-discount" className="small pr-0">
          {entry.discount > 0 && (
            <>
              <SignedCurrencyFormat value={-entry.discount} />
              <div className="text-muted">Already Deducted</div>
            </>
          )}
        </Col>
        <Col sm={1}>
          <ButtonGroup className="px-1 float-right">
            {onDelete && isEditing && (
              <DeleteWithConfirmation
                name="qa-delete-entry"
                color="light"
                className="p-0 bg-transparent border-0"
                onClick={() => onDelete(entry.id)}
              >
                <GoTrashcan />
              </DeleteWithConfirmation>
            )}
            {onAdd && isEditing && (
              <Button
                name="qa-add-entry"
                color="light"
                className="inv-click-target bg-transparent border-0"
                onClick={() => onAdd(entry)}
              >
                <GoPlus />
              </Button>
            )}
            {onRemove && isEditing && (
              <Button
                name="qa-remove-entry"
                color="light"
                className="inv-click-target bg-transparent border-0"
                onClick={() => onRemove(entry)}
              >
                <GoDash />
              </Button>
            )}
          </ButtonGroup>
        </Col>
      </Row>
    </div>
  );
}

BookingEntryLine.propTypes = {
  entry: bookingEntryShape,
  products: arrayOf(productShape.isRequired).isRequired,
  onDelete: func,
  showUpdateButton: bool,
  onAdd: func,
  onRemove: func,
  invoiceId: number,
  onIsSelected: func.isRequired,
  showSelectBox: bool.isRequired,
  onToggleSelection: func.isRequired,
  isEditing: bool,
};

BookingEntryLine.defaultProps = {
  entry: undefined,
  onDelete: undefined,
  showUpdateButton: false,
  onAdd: undefined,
  onRemove: undefined,
  invoiceId: undefined,
  isEditing: true,
};

export default BookingEntryLine;
